import { useState } from 'react';
import { FilterBar } from '../components/Filters';
import { MiniBar } from '../components/charts';
import {
  CAT_COLORS,
  downloadCsv,
  num,
  pct,
  useApi,
  useFilters,
  useQuery,
  type DriverPerfResponse,
  type DriverRow,
  type FiltersResponse,
} from '../lib/reports';

type SortKey = 'stops' | 'difot_pct' | 'completion_pct' | 'late_pct' | 'failed';

export function DriverPerformance() {
  const [filters, patch] = useFilters();
  const qs = useQuery(filters);
  const [sort, setSort] = useState<SortKey>('stops');

  const { data, error, loading } = useApi<DriverPerfResponse>(`/api/reports/driver-performance?${qs}`);
  const { data: options } = useApi<FiltersResponse>('/api/reports/difot/filters');

  const rows = [...(data?.rows ?? [])].sort((a, b) => (Number(b[sort] ?? 0) - Number(a[sort] ?? 0)));
  const maxStops = Math.max(1, ...rows.map((r) => r.stops));

  function exportRows() {
    downloadCsv(
      `amart-driver-performance-${filters.from}_${filters.to}.csv`,
      ['driver', 'stops', 'completed', 'failed', 'on_time', 'early', 'late', 'no_eta', 'difot_pct', 'completion_pct', 'late_pct'],
      rows.map((r) => [
        r.driver_name,
        r.stops,
        r.completed,
        r.failed,
        r.on_time,
        r.early,
        r.late,
        r.no_eta,
        r.difot_pct,
        r.completion_pct,
        r.late_pct,
      ]),
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reporting / Driver performance</p>
          <h1>Driver performance</h1>
          <p className="muted">
            {num(rows.length)} drivers · {filters.from} → {filters.to}
          </p>
        </div>
      </header>

      <FilterBar filters={filters} options={options} onChange={patch} onExport={exportRows} showDriver={false} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}

      {data && (
        <div className="card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <SortTh label="Stops" k="stops" sort={sort} setSort={setSort} />
                  <SortTh label="DIFOT %" k="difot_pct" sort={sort} setSort={setSort} />
                  <SortTh label="Completion %" k="completion_pct" sort={sort} setSort={setSort} />
                  <th>On time</th>
                  <th>Early</th>
                  <SortTh label="Late" k="late_pct" sort={sort} setSort={setSort} />
                  <th>No ETA</th>
                  <SortTh label="Failed" k="failed" sort={sort} setSort={setSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r: DriverRow) => (
                  <tr key={r.driver_name}>
                    <td className="cell-strong">{r.driver_name}</td>
                    <td>
                      <div className="cell-bar">
                        <MiniBar value={r.stops} max={maxStops} color={CAT_COLORS.early} />
                        <span>{num(r.stops)}</span>
                      </div>
                    </td>
                    <td className="cell-strong">{pct(r.difot_pct)}</td>
                    <td>{pct(r.completion_pct)}</td>
                    <td>{num(r.on_time)}</td>
                    <td>{num(r.early)}</td>
                    <td>{pct(r.late_pct)}</td>
                    <td>{num(r.no_eta)}</td>
                    <td>{num(r.failed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <p className="muted">No driver activity in this period.</p>}
        </div>
      )}
    </section>
  );
}

function SortTh({
  label,
  k,
  sort,
  setSort,
}: {
  label: string;
  k: SortKey;
  sort: SortKey;
  setSort: (k: SortKey) => void;
}) {
  return (
    <th className={`sortable${sort === k ? ' sorted' : ''}`} onClick={() => setSort(k)}>
      {label} {sort === k ? '▾' : ''}
    </th>
  );
}
